//@flow
import {ActionCreator, createReducer, buildCatchError} from '../../store/utils';
import {switchMap, map, debounceTime} from 'rxjs/operators';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';

import {getBannerGroupApi} from './api';
import {defaultProductCategoryLayout} from '../../entities';

import type {StoreInformationBlock} from '../../entities/store-information-block';
import type {RootState} from '..';
import type {
  BannerGroup,
  PopupBanner,
  HeaderBanner,
  Banner,
  ProductCategoryLayout,
  ProductCategoryAisleLayout,
  ProductCategoryMenuLayout,
} from '../../entities';
import {listStoreInformationBlocksApi} from './api/information-block';
export type HomePageState = {
  bannerGroups: BannerGroup[],
  bannerOpened?: Banner,
  popupBanners?: PopupBanner[],
  isFetchingBanners?: boolean,
  headerBanners: HeaderBanner[],

  productCategoryLayout: ProductCategoryLayout,

  storeInformationBlocks?: StoreInformationBlock[],
  isFetchingStoreInformationBlocks?: boolean,
};

export const initialState: HomePageState = {
  bannerGroups: [],
  headerBanners: [],
  productCategoryLayout: defaultProductCategoryLayout,
};

export type HomePageAction = {
  bannerGroups?: BannerGroup[],
  popupBanners?: PopupBanner[],
  bannerGroup?: BannerGroup,
  popupBanner?: PopupBanner,
  bannerGroupId?: string,
  popupBannerId?: string,
  popupBannerEnabled?: boolean,
  banner?: Banner,
  bannerId?: string,
  headerBanners?: HeaderBanner[],
  headerBanner?: HeaderBanner,
  headerBannerId?: string,
  headerBannerEnabled?: boolean,
  productCategoryLayout?: ProductCategoryLayout,
  categoryMenuLayout?: ProductCategoryMenuLayout,
  categoryAisleLayout?: ProductCategoryAisleLayout,

  storeInformationBlocks?: StoreInformationBlock[],
};

export class AddBannerGroup extends ActionCreator<HomePageAction> {}
export class UpdateBannerGroup extends ActionCreator<HomePageAction> {}
export class DeleteBannerGroup extends ActionCreator<HomePageAction> {}

export class OpenBanner extends ActionCreator<HomePageAction> {}
export class CloseBanner extends ActionCreator<HomePageAction> {}
export class DeleteBanner extends ActionCreator<HomePageAction> {}

export class AddBannerToGroup extends ActionCreator<HomePageAction> {}

export class GetBanners extends ActionCreator<HomePageAction> {}
export class GetBannersSuccess extends ActionCreator<HomePageAction> {}
export class GetBannersError extends ActionCreator<HomePageAction> {}

export class SetSavedPopupBanners extends ActionCreator<HomePageAction> {}
export class AddPopupBanner extends ActionCreator<HomePageAction> {}
export class SetEnabledPopupBanner extends ActionCreator<HomePageAction> {}
export class DeletePopupBanner extends ActionCreator<HomePageAction> {}

export class SetSavedHeaderBanners extends ActionCreator<HomePageAction> {}
export class AddHeaderBanner extends ActionCreator<HomePageAction> {}
export class DeleteHeaderBanner extends ActionCreator<HomePageAction> {}
export class UpdateHeaderBanner extends ActionCreator<HomePageAction> {}
export class EnableHeaderBanner extends ActionCreator<HomePageAction> {}

export class SetProductCategoryLayout extends ActionCreator<HomePageAction> {}

export class UpdateProductCategoryMenuLayout extends ActionCreator<HomePageAction> {}
export class UpdateProductCategoryAisleLayout extends ActionCreator<HomePageAction> {}

export class GetStoreInformationBlocks extends ActionCreator<HomePageAction> {}
export class GetStoreInformationBlocksSuccess extends ActionCreator<HomePageAction> {}
export class GetStoreInformationBlocksError extends ActionCreator<HomePageAction> {}

export const actions = {
  addBannerGroup: (bannerGroup: BannerGroup) =>
    new AddBannerGroup({bannerGroup}),
  updateBannerGroup: (bannerGroup: BannerGroup) =>
    new UpdateBannerGroup({bannerGroup}),
  deleteBannerGroup: (bannerGroupId: string) =>
    new DeleteBannerGroup({bannerGroupId}),
  addBannerToGroup: (bannerGroupId: string, banner: Banner) =>
    new AddBannerToGroup({banner, bannerGroupId}),
  openBanner: (banner: Banner) => new OpenBanner({banner}),
  deleteBanner: (bannerGroupId: string, bannerId: string) =>
    new DeleteBanner({bannerId, bannerGroupId}),

  closeBanner: () => new CloseBanner(),
  getBanners: () => new GetBanners(),

  setSavedPopupBanners: (popupBanners: PopupBanner[]) =>
    new SetSavedPopupBanners({popupBanners}),
  addPopupBanner: (popupBanner: PopupBanner) =>
    new AddPopupBanner({popupBanner}),
  deletePopupBanner: (popupBannerId: string) =>
    new DeletePopupBanner({popupBannerId}),
  setEnabledPopupBanner: (popupBannerId: string, enabled: boolean) =>
    new SetEnabledPopupBanner({popupBannerId, popupBannerEnabled: enabled}),

  setSavedHeaderBanners: (headerBanners: HeaderBanner[]) =>
    new SetSavedHeaderBanners({headerBanners}),
  addHeaderBanner: (headerBanner: HeaderBanner) =>
    new AddHeaderBanner({headerBanner}),
  deleteHeaderBanner: (headerBannerId: string) =>
    new DeleteHeaderBanner({headerBannerId}),

  updateHeaderBanner: (headerBannerId: string, headerBanner: HeaderBanner) =>
    new UpdateHeaderBanner({headerBannerId, headerBanner}),
  enableHeaderBanner: (headerBannerId: string, enabled: boolean) =>
    new EnableHeaderBanner({headerBannerId, headerBannerEnabled: enabled}),

  setProductCategoryLayout: (productCategoryLayout: ProductCategoryLayout) =>
    new SetProductCategoryLayout({productCategoryLayout}),

  updateProductCategoryMenuLayout: (
    categoryMenuLayout: ProductCategoryMenuLayout
  ) => new UpdateProductCategoryMenuLayout({categoryMenuLayout}),

  updateProductCategoryAisleLayout: (
    categoryAisleLayout: ProductCategoryAisleLayout
  ) => new UpdateProductCategoryAisleLayout({categoryAisleLayout}),

  getStoreInformationBlocks: () => new GetStoreInformationBlocks(),
};
export default createReducer(initialState, {
  [AddBannerToGroup.type]: (
    state: HomePageState,
    {banner, bannerGroupId}: HomePageAction
  ): HomePageState => {
    if (!banner) return state;
    return {
      ...state,
      bannerGroups: state.bannerGroups.map(bannerGroup => {
        if (bannerGroup.id !== bannerGroupId) return bannerGroup;
        return {
          ...bannerGroup,
          banners: [...bannerGroup.banners, banner],
        };
      }),
    };
  },
  [OpenBanner.type]: (
    state: HomePageState,
    {banner}: HomePageAction
  ): HomePageState => ({
    ...state,
    bannerOpened: banner,
  }),
  [CloseBanner.type]: (state: HomePageState): HomePageState => ({
    ...state,
    bannerOpened: undefined,
  }),
  [GetBanners.type]: (state: HomePageState): HomePageState => ({
    ...state,
    isFetchingBanners: true,
  }),
  [GetBannersSuccess.type]: (
    state: HomePageState,
    {bannerGroups}: HomePageAction
  ): HomePageState => ({
    ...state,
    bannerGroups: bannerGroups || [],
    isFetchingBanners: false,
  }),
  [GetBannersError.type]: (state: HomePageState): HomePageState => ({
    ...state,
    isFetchingBanners: false,
    banners: [],
  }),
  [DeleteBanner.type]: (
    state: HomePageState,
    {bannerId, bannerGroupId}: HomePageAction
  ): HomePageState => {
    return {
      ...state,
      bannerGroups: state.bannerGroups.map(bannerGroup => {
        if (bannerGroupId !== bannerGroup.id) return bannerGroup;

        return {
          ...bannerGroup,
          banners: bannerGroup.banners.filter(banner => banner.id !== bannerId),
        };
      }),
    };
  },
  [AddBannerGroup.type]: (
    state: HomePageState,
    {bannerGroup}: HomePageAction
  ): HomePageState => {
    if (!bannerGroup) return state;
    return {
      ...state,
      bannerGroups: [...state.bannerGroups, bannerGroup],
    };
  },
  [UpdateBannerGroup.type]: (
    state: HomePageState,
    {bannerGroup}: HomePageAction
  ): HomePageState => {
    if (!bannerGroup) return state;
    return {
      ...state,
      bannerGroups: state.bannerGroups.map(group => {
        if (group.id === bannerGroup?.id) {
          return bannerGroup;
        }
        return group;
      }),
    };
  },
  [DeleteBannerGroup.type]: (
    state: HomePageState,
    {bannerGroupId}: HomePageAction
  ): HomePageState => ({
    ...state,
    bannerGroups: state.bannerGroups.filter(
      bannerGroup => bannerGroup.id !== bannerGroupId
    ),
  }),
  [SetSavedPopupBanners.type]: (
    state: HomePageState,
    {popupBanners}: HomePageAction
  ): HomePageState => ({
    ...state,
    popupBanners,
  }),
  [AddPopupBanner.type]: (
    state: HomePageState,
    {popupBanner}: HomePageAction
  ): HomePageState => {
    if (!popupBanner) return state;

    return {
      ...state,
      popupBanners: [...(state.popupBanners || []), popupBanner],
    };
  },
  [DeletePopupBanner.type]: (
    state: HomePageState,
    {popupBannerId}: HomePageAction
  ): HomePageState => ({
    ...state,
    popupBanners: state.popupBanners?.filter(
      popupBanner => popupBanner.id !== popupBannerId
    ),
  }),
  [SetEnabledPopupBanner.type]: (
    state,
    {popupBannerId, popupBannerEnabled}: HomePageAction
  ): HomePageState => ({
    ...state,
    popupBanners: state.popupBanners.map(popupBanner => {
      if (popupBanner.id === popupBannerId && popupBannerEnabled) {
        return {
          ...popupBanner,
          enabled: true,
        };
      }
      return {
        ...popupBanner,
        enabled: false,
      };
    }),
  }),
  [SetSavedHeaderBanners.type]: (
    state: HomePageState,
    {headerBanners}: HomePageAction
  ): HomePageState => {
    if (!headerBanners) return state;
    return {
      ...state,
      headerBanners,
    };
  },
  [AddHeaderBanner.type]: (
    state: HomePageState,
    {headerBanner}: HomePageAction
  ): HomePageState => {
    if (!headerBanner) return state;
    return {
      ...state,
      headerBanners: [...(state.headerBanners || []), headerBanner],
    };
  },
  [DeleteHeaderBanner.type]: (
    state: HomePageState,
    {headerBannerId}: HomePageAction
  ): HomePageState => ({
    ...state,
    headerBanners: state.headerBanners.filter(
      headerBanner => headerBanner.id !== headerBannerId
    ),
  }),
  [UpdateHeaderBanner.type]: (
    state,
    {headerBannerId, headerBanner}: HomePageAction
  ): HomePageState => ({
    ...state,
    headerBanners: state.headerBanners.map(banner => {
      if (banner.id === headerBannerId) {
        return headerBanner;
      }
      return banner;
    }),
  }),
  [EnableHeaderBanner.type]: (
    state,
    {headerBannerId, headerBannerEnabled}: HomePageAction
  ): HomePageState => ({
    ...state,
    headerBanners: state.headerBanners.map(banner => {
      if (banner.id === headerBannerId) {
        return {
          ...banner,
          enabled: headerBannerEnabled,
        };
      }
      return {
        ...banner,
        enabled: false,
      };
    }),
  }),
  [SetProductCategoryLayout.type]: (
    state: HomePageState,
    {productCategoryLayout}: HomePageAction
  ): HomePageState => {
    if (!productCategoryLayout) return state;

    return {
      ...state,
      productCategoryLayout,
    };
  },

  [UpdateProductCategoryMenuLayout.type]: (
    state: HomePageState,
    {categoryMenuLayout}: HomePageAction
  ): HomePageState => {
    if (!categoryMenuLayout) return state;

    return {
      ...state,
      productCategoryLayout: {
        ...state.productCategoryLayout,
        productCategoryMenu: categoryMenuLayout,
      },
    };
  },

  [UpdateProductCategoryAisleLayout.type]: (
    state: HomePageState,
    {categoryAisleLayout}: HomePageAction
  ): HomePageState => {
    if (!categoryAisleLayout) return state;

    return {
      ...state,
      productCategoryLayout: {
        ...state.productCategoryLayout,
        productCategoryAisle: categoryAisleLayout,
      },
    };
  },
  [GetStoreInformationBlocks.type]: (state: HomePageState): HomePageState => ({
    ...state,
    isFetchingStoreInformationBlocks: true,
  }),
  [GetStoreInformationBlocksSuccess.type]: (
    state: HomePageState,
    {storeInformationBlocks}: HomePageAction
  ): HomePageState => ({
    ...state,
    storeInformationBlocks: storeInformationBlocks || [],
    isFetchingStoreInformationBlocks: false,
  }),
  [GetStoreInformationBlocksError.type]: (
    state: HomePageState
  ): HomePageState => ({
    ...state,
    isFetchingStoreInformationBlocks: false,
    storeInformationBlocks: [],
  }),
});

export function getBannersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(GetBanners.type),
    switchMap(() => {
      return getBannerGroupApi().pipe(
        map(
          bannerGroups =>
            new GetBannersSuccess({
              bannerGroups,
            })
        ),
        buildCatchError(GetBannersError)
      );
    })
  );
}

export function getStoreInformationBlocksEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(GetStoreInformationBlocks.type),
    debounceTime(500),
    switchMap(() => {
      return listStoreInformationBlocksApi().pipe(
        map(
          storeInformationBlocks =>
            new GetStoreInformationBlocksSuccess({
              storeInformationBlocks,
            })
        ),
        buildCatchError(GetStoreInformationBlocksError)
      );
    })
  );
}
